<script setup>
import { provide, ref } from 'vue';
import StartNode from './nodes/StartNode.vue';
import NoMatchNode from './nodes/NoMatchNode.vue';
import InteractiveNode from './nodes/InteractiveNode.vue';
import TextNode from './nodes/TextNode.vue';
import CarouselNode from './nodes/CarouselNode.vue';
import KeyboardNode from './nodes/KeyboardNode.vue';
import ContactNode from './nodes/ContactNode.vue';
import MediaNode from './nodes/MediaNode.vue';
import BranchNode from './nodes/BranchNode.vue';
import StructuralNode from './nodes/StructuralNode.vue';
import FallbackNode from './nodes/FallbackNode.vue';
import NodeFloatingToolbar from './ui/NodeFloatingToolbar.vue';
import { flowThreadContextKey } from './nodes/flowThreadContext';
import {
  flowThreadBranchTypes,
  flowThreadMessageTypes,
  flowThreadStructuralTypes,
} from './nodes/flowThreadConstants';
import { useFlowThreadContext } from './nodes/useFlowThreadContext';
import FlowThreadChildren from './FlowThreadChildren.vue';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  selectedNodeId: {
    type: String,
    default: '',
  },
  quickInsertItems: {
    type: Array,
    default: () => [],
  },
});
const emit = defineEmits([
  'select',
  'insertAfter',
  'updateNode',
  'deleteNode',
  'duplicateNode',
  'addInteractiveButton',
  'moveInteractiveButton',
  'addInteractiveListRow',
  'addInteractiveListSection',
  'quickInsert',
]);

const rootRef = ref(null);
const interactiveHeaderUploadRef = ref(null);
const messageNodeTypes = flowThreadMessageTypes;
const branchNodeTypes = flowThreadBranchTypes;
const structuralNodeTypes = flowThreadStructuralTypes;

defineOptions({
  name: 'FlowThreadNode',
});

const {
  uiCopy,
  node,
  isSelected,
  bubbleShellClass,
  nodeWidthClass,
  continuationItem,
  listKeyboardBranch,
  isInteractiveListNode,
  listSections,
  visibleStructuredBranches,
  interactiveButtons,
  interactiveButtonContinuationItems,
  canAddInteractiveButton,
  listRows,
  canAddRowToSection,
  canAddAnotherSection,
  branchInsertItems,
  quickInsertMenuTarget,
  hasOpenInteractiveButtonMenu,
  hasOpenQuickInsertMenu,
  updateNode,
  updateInteractiveHeader,
  setInteractiveHeaderMode,
  triggerInteractiveHeaderUpload,
  handleInteractiveHeaderUpload,
  updateListKeyboardNode,
  updateListSectionNode,
  updateBranchNode,
  toggleQuickInsertMenu,
  insertBranchNode,
  buttonDraftValue,
  updateButtonDraft,
  persistButtonDraft,
  branchContinuationSummary,
  interactiveButtonPosition,
} = useFlowThreadContext({
  props,
  emit,
  rootRef,
  interactiveHeaderUploadRef,
});

provide(flowThreadContextKey, {
  uiCopy,
  quickInsertMenuTarget,
  branchInsertItems,
  hasOpenQuickInsertMenu,
  hasOpenInteractiveButtonMenu,
  canAddInteractiveButton,
  canAddRowToSection,
  canAddAnotherSection,
  interactiveHeaderUploadRef,
  updateNode,
  emitUpdateNode: payload => emit('updateNode', payload),
  updateInteractiveHeader,
  setInteractiveHeaderMode,
  triggerInteractiveHeaderUpload,
  handleInteractiveHeaderUpload,
  updateListKeyboardNode,
  updateListSectionNode,
  updateBranchNode,
  toggleQuickInsertMenu,
  insertBranchNode,
  buttonDraftValue,
  updateButtonDraft,
  persistButtonDraft,
  branchContinuationSummary,
  interactiveButtonPosition,
  selectNode: nodeId => emit('select', nodeId),
  requestDeleteNode: nodeId => emit('deleteNode', nodeId),
  requestAddInteractiveButton: nodeId => emit('addInteractiveButton', nodeId),
  requestMoveInteractiveButton: payload =>
    emit('moveInteractiveButton', payload),
  requestAddInteractiveListRow: nodeId => emit('addInteractiveListRow', nodeId),
  requestAddInteractiveListSection: nodeId =>
    emit('addInteractiveListSection', nodeId),
});
</script>

<template>
  <div ref="rootRef" class="flex flex-col items-center gap-2.5">
    <div
      class="group relative w-full"
      :data-node-id="props.item.node?.id"
      :class="
        messageNodeTypes.includes(node.type) || node.type === 'noMatch'
          ? `ms-auto ${nodeWidthClass}`
          : `mx-auto ${nodeWidthClass}`
      "
    >
      <NodeFloatingToolbar
        :is-visible="isSelected"
        :delete-title="uiCopy.deleteNode"
        @delete="emit('deleteNode', node.id)"
      />

      <div
        class="rounded-[1.25rem] border bg-white transition-all duration-200"
        :class="[
          bubbleShellClass,
          hasOpenQuickInsertMenu ? 'overflow-visible' : 'overflow-hidden',
        ]"
        @click="emit('select', node.id)"
      >
        <StartNode
          v-if="node.type === 'start'"
          :node="node"
          :is-selected="isSelected"
        />

        <NoMatchNode
          v-else-if="node.type === 'noMatch'"
          :node="node"
          :is-selected="isSelected"
        />

        <InteractiveNode
          v-else-if="node.type === 'interactive'"
          :node="node"
          :is-selected="isSelected"
          :is-interactive-list-node="isInteractiveListNode"
          :list-keyboard-branch="listKeyboardBranch"
          :list-sections="listSections"
          :interactive-buttons="interactiveButtons"
        />

        <TextNode
          v-else-if="node.type === 'text' || node.type === 'locationRequest'"
          :node="node"
          :is-selected="isSelected"
        />

        <CarouselNode v-else-if="node.type === 'carousel'" :node="node" />

        <KeyboardNode
          v-else-if="node.type === 'keyboard'"
          :node="node"
          :is-selected="isSelected"
          :list-rows="listRows"
        />

        <ContactNode
          v-else-if="node.type === 'contact'"
          :node="node"
          :is-selected="isSelected"
        />

        <MediaNode
          v-else-if="
            node.type === 'image' ||
            node.type === 'video' ||
            node.type === 'audio' ||
            node.type === 'file'
          "
          :node="node"
        />

        <BranchNode
          v-else-if="branchNodeTypes.includes(node.type)"
          :node="node"
          :is-selected="isSelected"
        />

        <StructuralNode
          v-else-if="structuralNodeTypes.includes(node.type)"
          :node="node"
          :is-selected="isSelected"
        />

        <FallbackNode v-else :node="node" />
      </div>
    </div>

    <FlowThreadChildren
      :node-type="node.type"
      :continuation-item="continuationItem"
      :visible-structured-branches="visibleStructuredBranches"
      :interactive-button-continuation-items="
        interactiveButtonContinuationItems
      "
      :quick-insert-items="quickInsertItems"
      :selected-node-id="selectedNodeId"
      @select="emit('select', $event)"
      @insert-after="emit('insertAfter', $event)"
      @update-node="emit('updateNode', $event)"
      @delete-node="emit('deleteNode', $event)"
      @duplicate-node="emit('duplicateNode', $event)"
      @add-interactive-button="emit('addInteractiveButton', $event)"
      @move-interactive-button="emit('moveInteractiveButton', $event)"
      @add-interactive-list-row="emit('addInteractiveListRow', $event)"
      @add-interactive-list-section="emit('addInteractiveListSection', $event)"
      @quick-insert="emit('quickInsert', $event)"
    />
  </div>
</template>
